import { use, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { trackAnalyticsCustomEvent } from '@ifixit/analytics';
import { useStableCallback } from '@ifixit/ui/hooks/use-stable-callback';
import { sendReactNativeMessage } from '@ifixit/utils';
import { WorkbenchPrompt } from '@ifixit/workbench/workbench-prompt';
import { GuideCompletionContext } from './GuideComplete';

/** The guide's device page, sent only when it can go on a Workbench. */
export interface WorkbenchDevice {
   wikiid: number;
   title: string;
   image: string | null;
}

/** Attach the conclusion's complete button to the PHP markup in complete_button.phtml. */
export function GuideConclusionInteractions() {
   const completion = use(GuideCompletionContext);
   const complete = useStableCallback((event: Event) => {
      event.preventDefault();
      completion.complete(App.guideid, 'conclusion', () =>
         sendReactNativeMessage({
            type: 'review_prompt_requested',
            trigger: 'Guide Completion Button',
         })
      );
   });
   const removeComplete = useStableCallback((event: Event) => {
      event.preventDefault();
      completion.removeComplete(App.guideid);
   });

   useEffect(() => {
      const toggle = (selector: string, hidden: boolean) =>
         document.querySelector(selector)?.classList?.toggle('hidden!', hidden);
      toggle('#successButton', completion.isCompleted);
      toggle('#doneButton', !completion.isCompleted);
      toggle('#cancelComplete', !completion.isCompleted);
   }, [completion.isCompleted]);

   useEffect(() => {
      const successButton = document.querySelector<HTMLButtonElement>('#successButton');
      if (successButton) successButton.disabled = completion.isPending;
   }, [completion.isPending]);

   useEffect(() => {
      const successButton = document.querySelector<HTMLButtonElement>('#successButton');
      const cancelComplete = document.querySelector('#cancelComplete');
      successButton?.addEventListener('click', complete);
      cancelComplete?.addEventListener('click', removeComplete);
      return () => {
         successButton?.removeEventListener('click', complete);
         cancelComplete?.removeEventListener('click', removeComplete);
         if (successButton) successButton.disabled = true;
      };
   }, [complete, removeComplete]);

   useEffect(() => {
      const guideComplete = document.querySelector('.guide-complete');
      if (!guideComplete) return;
      const observer = new IntersectionObserver(entries => {
         if (entries.every(entry => !entry.isIntersecting)) return;
         trackAnalyticsCustomEvent({
            eventCategory: 'Guide Completion',
            eventAction: 'Guide Completion - Scroll to Complete - Scroll',
         });
         observer.disconnect();
      });
      observer.observe(guideComplete);
      return () => observer.disconnect();
   }, []);

   return null;
}

/** Offer to save the guide's device right where someone marked the guide done. */
export function GuideWorkbenchPrompt({ device }: { device: WorkbenchDevice | null }) {
   const { completedFrom } = use(GuideCompletionContext);
   const slot = useMemo(
      () =>
         completedFrom
            ? document.querySelector(`[data-guide-workbench-prompt="${completedFrom}"]`)
            : null,
      [completedFrom]
   );

   if (!device || !slot) return null;

   return createPortal(
      <WorkbenchPrompt
         wikiid={device.wikiid}
         title={device.title}
         image={device.image}
         source="guide_complete"
      />,
      slot
   );
}
